<template>
  <AisRangeInput v-slot="{ currentRefinement, range, refine, canRefine }" :attribute="filter.attributePath" class="range-filter">
    <div v-if="!canRefine" />
    <FilterDropdown v-else :label="filter.label">
      <template #selection>
        {{ previewText(currentRefinement, range) }}
      </template>
      <div class="pt-8 pb-4 px-6 lg:px-8">
        <VueSlider
          :min="range.min"
          :max="range.max"
          :lazy="true"
          :tooltip="'always'"
          :dot-size="24"
          :silent="true"
          :disabled="!canRefine"
          :tooltip-formatter="formatTooltip"
          :model-value="[currentRefinement.min || range.min, currentRefinement.max || range.max]"
          @change="refine({ min: $event[0], max: $event[1] })"
        />
      </div>

      <div class="flex gap-4 justify-center items-center">
        <div class="grid items-center">
          <input
            type="number"
            :min="range.min"
            :max="range.max"
            :placeholder="range.min+''"
            :disabled="!canRefine"
            :name="filter.urlParam+'-min'"
            class="border rounded bg-surface-raised py-1 px-3 col-start-1 row-start-1 pr-6 w-20 outline-none focus:border-grey-950 focus:shadow-md"
            :value="currentRefinement.min"
            @change="refine({
              min: +($event.currentTarget as HTMLInputElement)?.value,
              max: currentRefinement.max
            })"
          >
          <span class="col-start-1 row-start-1 text-muted text-xs pointer-events-none text-center w-8 ml-auto">
            {{ filter.unit }}
          </span>
        </div>
        <div>
          &ndash;
        </div>
        <div class="grid items-center">
          <input
            type="number"
            :min="range.min"
            :max="range.max"
            :placeholder="range.max+''"
            :disabled="!canRefine"
            :name="filter.urlParam+'-max'"
            class="border rounded bg-surface-raised py-1 px-3 col-start-1 row-start-1 pr-6 w-20 outline-none focus:border-grey-950 focus:shadow-md"
            :value="currentRefinement.max"
            @change="refine({
              min: currentRefinement.min,
              max: +($event.currentTarget as HTMLInputElement)?.value
            })"
          >
          <span class="col-start-1 row-start-1 text-muted text-xs pointer-events-none text-center w-8 ml-auto">
            {{ filter.unit }}
          </span>
        </div>
      </div>
    </FilterDropdown>
  </AisRangeInput>
</template>

<script setup lang="ts">
import VueSlider from 'vue-3-slider-component'
import FilterDropdown from '~/components/category/product-filters/FilterDropdown.vue'
import type { ProductFilter } from '~/types/productFilter'

interface Range {
  min?: number
  max?: number
}
interface CurrentRefinement {
  min: Range['min']
  max: Range['max']
}

const props = defineProps({
  filter: {
    type: Object as () => ProductFilter,
    required: true
  }
})

const previewText = (currentRefinement: CurrentRefinement, range: Range) => (currentRefinement?.min && currentRefinement?.max && (currentRefinement?.min !== range?.min || currentRefinement?.max !== range?.max))
  ? currentRefinement?.min + ' ' + props.filter.unit + ' – ' + currentRefinement?.max + ' ' + props.filter.unit
  : ''

const formatTooltip = (v: any) => `${v + ' ' + props.filter.unit}`
</script>

<style lang="scss">
@import 'vue-slider-component/dist-css/vue-slider-component.css';
.range-filter {
  .vue-slider {
    .vue-slider-rail {
      @apply bg-grey-100;
      .vue-slider-process {
        @apply bg-red-700;
      }
      .vue-slider-dot-handle {
        @apply rounded-full shadow-none bg-red-800 z-2;
        &-focus {
          @apply bg-red-700 shadow-md;
        }
      }
      .vue-slider-dot-tooltip-inner {
        @apply inline-flex whitespace-nowrap text-sm px-2 py-1 bg-grey-50 border-grey-50 text-secondary font-medium rounded ring-2 ring-surface-raised;
      }
    }
  }
  input[type=number]::-webkit-inner-spin-button,
  input[type=number]::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
}
</style>
